<script setup>
import { useResizeObserver } from '@vueuse/core';
import { X } from 'lucide-vue-next';

import InviteNotificationRow from '@/components/notifications/InviteNotificationRow.vue';
import { useConnectionAlertsStore } from '@/stores/connectionAlerts';
import { useReceivedInvitesStore } from '@/stores/receivedInvites';

const connectionAlertsStore = useConnectionAlertsStore();
const receivedInvitesStore = useReceivedInvitesStore();

// The bar shows when there's anything to show: a connection/plan alert or a pending invite.
const hasContent = computed(
  () => connectionAlertsStore.hasNotifications || receivedInvitesStore.hasInvites,
);

const wrapperRef = ref(null);

const updateNotificationBarHeight = (height) => {
  document.documentElement.style.setProperty('--notification-bar-height', `${height}px`);
  // Notify v-sticky directive and other listeners that depend on notification bar height
  window.dispatchEvent(new Event('resize'));
};

useResizeObserver(wrapperRef, (entries) => {
  const entry = entries[0];
  updateNotificationBarHeight(entry.contentRect.height);
});

watch(hasContent, (has) => {
  if (!has) {
    updateNotificationBarHeight(0);
  }
});

onBeforeUnmount(() => {
  updateNotificationBarHeight(0);
});

// Matches http(s)://, mailto: and root-relative paths; blocks javascript:, data:, etc.
const SAFE_HREF_RE = /^(https?:\/\/|mailto:|\/)/i;

/**
 * Parses a string with markdown-style links [text](url) into an array of
 * { type: 'text' | 'link' } tokens. Rendered via template, not v-html, to
 * prevent XSS from backend-supplied notification messages.
 */
const parseMessage = (message) => {
  const tokens = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(message)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', value: message.slice(lastIndex, match.index) });
    }
    const href = match[2].trim();
    tokens.push({
      type: 'link',
      text: match[1],
      href: SAFE_HREF_RE.test(href) ? href : '#',
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < message.length) {
    tokens.push({ type: 'text', value: message.slice(lastIndex) });
  }
  return tokens;
};
</script>

<template>
  <div
    v-if="hasContent"
    ref="wrapperRef"
    class="notification-bar-wrapper"
  >
    <InviteNotificationRow
      v-for="invite in receivedInvitesStore.invites"
      :key="invite.code"
      :invite="invite"
    />
    <div
      v-for="notification in connectionAlertsStore.notifications"
      :key="notification.event_id"
      class="notification-bar"
      :class="[
        `notification-bar--${notification.type}`,
        { 'notification-bar--dismissable': notification.dismiss_duration != null },
      ]"
    >
      <span v-no-translate class="notification-bar__message">
        <template
          v-for="(token, i) in parseMessage(notification.message)"
          :key="i"
        >
          <a
            v-if="token.type === 'link'"
            :href="token.href"
          >{{ token.text }}</a>
          <template v-else>{{ token.value }}</template>
        </template>
      </span>
      <button
        v-if="notification.dismiss_duration != null"
        class="notification-bar__close"
        @click="connectionAlertsStore.dismissNotification(notification.event_id)"
      >
        <X :size="16" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notification-bar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 998;
}

.notification-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 16px;
  text-align: center;
  position: relative;

  &--dismissable {
    padding-right: 40px;
  }

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  &--info {
    background-color: var(--blue);
    color: var(--white);
  }

  &--success {
    background-color: var(--green);
    color: var(--white);
  }

  &--warning {
    background-color: var(--orange);
    color: var(--black);
  }

  &--error {
    background-color: var(--red);
    color: var(--white);
  }

  &__message {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: inherit;

    :deep(a) {
      color: inherit;
      text-decoration: underline;
      font-weight: 600;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  &__close {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
